import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Key, MapPin, MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminFMSSettings = () => {
  const [settings, setSettings] = useState({
    google_maps_api_key: '',
    teltonika_server_url: '',
    teltonika_port: '5010',
    sms_provider: 'twilio',
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_phone_number: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/fms-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setSettings({ ...settings, ...response.data });
      }
    } catch (error) {
      console.log('Settings not found, using defaults');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/fms-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('FMS-Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold mb-8" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
          FMS Einstellungen
        </h2>

        {/* Google Maps API */}
        <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
              <MapPin className="w-5 h-5" style={{ color: '#7CD4FD' }} />
              Google Maps API
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#9CA3AF' }}>
                  API Key
                </label>
                <input
                  type="password"
                  value={settings.google_maps_api_key}
                  onChange={(e) => setSettings({ ...settings, google_maps_api_key: e.target.value })}
                  placeholder="AIzaSy..."
                  className="w-full px-4 py-2 rounded-lg"
                  style={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    color: '#E6EDF3'
                  }}
                />
                <p className="text-xs mt-2" style={{ color: '#6B7280' }}>
                  Für GPS-Tracking und Kartenanzeige. 
                  <a 
                    href="https://console.cloud.google.com/apis" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="ml-1"
                    style={{ color: '#7CD4FD' }}
                  >
                    API-Key erstellen →
                  </a>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Teltonika Server */}
        <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
              <Key className="w-5 h-5" style={{ color: '#7CD4FD' }} />
              Teltonika Server
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#9CA3AF' }}>
                  Server URL (optional)
                </label>
                <input
                  type="text"
                  value={settings.teltonika_server_url}
                  onChange={(e) => setSettings({ ...settings, teltonika_server_url: e.target.value })}
                  placeholder="https://your-server.com"
                  className="w-full px-4 py-2 rounded-lg"
                  style={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    color: '#E6EDF3'
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#9CA3AF' }}>
                  TCP/UDP Port
                </label>
                <input
                  type="text"
                  value={settings.teltonika_port}
                  onChange={(e) => setSettings({ ...settings, teltonika_port: e.target.value })}
                  placeholder="5010"
                  className="w-full px-4 py-2 rounded-lg"
                  style={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    color: '#E6EDF3'
                  }}
                />
                <p className="text-xs mt-2" style={{ color: '#6B7280' }}>
                  Standard: 5010 (für Teltonika FMM003)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SMS Provider (Twilio) */}
        <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
              <MessageSquare className="w-5 h-5" style={{ color: '#7CD4FD' }} />
              SMS Provider (Twilio)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#9CA3AF' }}>
                  Account SID
                </label>
                <input
                  type="password"
                  value={settings.twilio_account_sid}
                  onChange={(e) => setSettings({ ...settings, twilio_account_sid: e.target.value })}
                  placeholder="AC..."
                  className="w-full px-4 py-2 rounded-lg"
                  style={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    color: '#E6EDF3'
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#9CA3AF' }}>
                  Auth Token
                </label>
                <input
                  type="password"
                  value={settings.twilio_auth_token}
                  onChange={(e) => setSettings({ ...settings, twilio_auth_token: e.target.value })}
                  placeholder="..."
                  className="w-full px-4 py-2 rounded-lg"
                  style={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    color: '#E6EDF3'
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#9CA3AF' }}>
                  Phone Number
                </label>
                <input
                  type="text"
                  value={settings.twilio_phone_number}
                  onChange={(e) => setSettings({ ...settings, twilio_phone_number: e.target.value })}
                  placeholder="+4915..."
                  className="w-full px-4 py-2 rounded-lg"
                  style={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    color: '#E6EDF3'
                  }}
                />
                <p className="text-xs mt-2" style={{ color: '#6B7280' }}>
                  Für Benachrichtigungen bei Störungen, Wartungen etc. 
                  <a 
                    href="https://www.twilio.com/console" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="ml-1"
                    style={{ color: '#7CD4FD' }}
                  >
                    Twilio Console →
                  </a>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="px-8 py-6"
            style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Speichern...' : 'Einstellungen speichern'}
          </Button>
        </div>

        {/* Info Box */}
        <Card className="mt-8" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', borderLeft: '4px solid #7CD4FD' }}>
          <CardContent className="p-6">
            <h3 className="font-semibold mb-2" style={{ color: '#7CD4FD' }}>
              ℹ️ Wichtige Hinweise
            </h3>
            <ul className="space-y-2" style={{ color: '#9CA3AF', fontSize: '14px' }}>
              <li>• Google Maps API wird für GPS-Tracking und Kartenanzeige benötigt</li>
              <li>• Teltonika Server-Einstellungen sind optional (System nutzt internes Backend)</li>
              <li>• SMS-Benachrichtigungen erfordern einen aktiven Twilio-Account</li>
              <li>• Alle API-Keys werden verschlüsselt gespeichert</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
